import { useDocumentTitle } from "@/lib/use-document-title";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useState } from "react";
import { ShieldCheck, HeartHandshake, Clock, Tag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { translateAuthError } from "@/lib/auth-errors";
import { useAuth } from "@/lib/auth-context";
import { BackLink } from "@/components/BackLink";
import { AccessIcon } from "@/components/AccessIcon";

function LoginPage() {
  useDocumentTitle("Logga in — Lagamobilen Access");
  const navigate = useNavigate();
  const { session, member, staff, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && session && (member || (staff && staff.active))) {
    return <Navigate to="/access/dashboard" replace />;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    if (!email || !pwd) { setErr("Ange e-post och lösenord"); return; }
    setBusy(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password: pwd });
    if (error) { setBusy(false); setErr(translateAuthError(error.message)); return; }
    const uid = data.user!.id;
    const [{ data: memberRow }, { data: staffRow }] = await Promise.all([
      supabase.from("members").select("id").eq("user_id", uid).maybeSingle(),
      supabase.from("staff" as any).select("id, active").eq("user_id", uid).maybeSingle(),
    ]);
    const isStaff = !!staffRow && (staffRow as any).active;
    if (!memberRow && !isStaff) {
      await supabase.auth.signOut();
      setBusy(false);
      setErr("Det här kontot är inte kopplat till ett ACCESS-medlemskap.");
      return;
    }
    navigate("/access/dashboard");
  }

  const benefits = [
    { icon: HeartHandshake, label: "Personlig teknikhjälp" },
    { icon: ShieldCheck, label: "Fri felsökning" },
    { icon: Clock, label: "Prioriterad service" },
    { icon: Tag, label: "20% rabatt på reparationer" },
  ];

  return (
    <div className="min-h-screen bg-[var(--color-soft)]">


      <main className="flex items-center justify-center px-6 sm:px-10 py-12 lg:py-16">
        <div className="w-full max-w-md">
          <div className="mb-6"><BackLink fallback="/access" /></div>
          <div className="flex flex-col items-center text-center mb-8">
            <AccessIcon size={56} className="text-[var(--color-gold)]" title="Lagamobilen Access" />
            <div className="mt-3 font-display tracking-[0.35em] text-[10px] text-muted-foreground uppercase">Lagamobilen · Access</div>
          </div>
          <h1 className="!text-3xl text-foreground text-center">Logga in</h1>
          <p className="text-sm text-muted-foreground mt-2 text-center">Välkommen tillbaka till din medlemsportal.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="block text-xs font-display font-semibold text-foreground mb-1.5">E-post</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full rounded-lg border border-border focus:border-primary px-4 py-2.5 text-sm focus:outline-none transition"
                placeholder="din@email.se"
              />
            </div>
            <div>
              <label className="block text-xs font-display font-semibold text-foreground mb-1.5">Lösenord</label>
              <input
                type="password"
                value={pwd}
                onChange={e => setPwd(e.target.value)}
                className="w-full rounded-lg border border-border focus:border-primary px-4 py-2.5 text-sm focus:outline-none transition"
              />
            </div>

            <div className="flex items-center justify-end text-sm">
              <Link to="/access/glomt-losenord" className="text-primary hover:underline">
                Glömt lösenord?
              </Link>
            </div>

            {err && <p className="text-destructive text-xs">{err}</p>}

            <button
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center py-3 rounded-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition text-sm font-display font-semibold tracking-wide disabled:opacity-60"
            >
              {busy ? "Loggar in…" : "Logga in"}
            </button>
          </form>

          <p className="text-sm text-muted-foreground text-center mt-8">
            Medlem men inget konto ännu?{" "}
            <Link to="/access/skapa-konto" className="text-primary hover:underline font-semibold">
              Aktivera ditt konto
            </Link>
          </p>

          <p className="text-xs text-muted-foreground text-center mt-6">
            Är du butikspersonal?{" "}
            <Link to="/af-portal" className="text-primary hover:underline">Gå till ÅF-portalen</Link>
          </p>
        </div>
      </main>
    </div>
  );
}

export default LoginPage;
